/**
 * Module responsible to handle Rudderstack SDK load.
 *  - Rudderstack SDK asset is fetched on 'scripts.rudderstack.liquid'.
 *  - OneTrust is integrated with Rudderstack using pre-consent user tracking, this means Ruddertack collect data before the user gave consent
 *     and then send it after the user provides the consent.
 *
 * Documentation:
 *   - OneTrust Integration: https://www.rudderstack.com/docs/data-governance/consent-management/onetrust/javascript/
 *   - Rudderstack Load Options: https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/load-js-sdk/
 *   - Rudderstack Pre-Consent: https://www.rudderstack.com/docs/data-governance/consent-management/onetrust/javascript/#pre-consent-user-tracking
 *
 */

import 'vite/modulepreload-polyfill';

import * as DataDogLogs from '~/lib/datadog/datadog';

// Pull environment types from window object ie: development, production, staging
const ENVIRONMENTS = window.ALLBIRDS.environment.types;

// Map of Rudderstack App keys by environment and shopId, ie: 'production-UK', 'production-US', 'development', 'staging'
const RUDDERSTACK_APP_KEYS = {
  [`${ENVIRONMENTS.PRODUCTION}-UK`]: '1wsEvk2JSySDpu4GkRrODFa3wfu',
  [`${ENVIRONMENTS.PRODUCTION}-US`]: '1wsEr0lioiZzgx5iZd40tVWiUJI',
  [ENVIRONMENTS.DEVELOPMENT]: '1xEmwnbSXytFtBkBAc7P0e79vg1',
  [ENVIRONMENTS.STAGING]: '1rPF2XhPLMKZS39MROI5nrujMIY',
};

// Constants for Rudderstack
const RS_CONFIG_URL = 'https://api.rudderstack.com';
const RS_DATA_PLANE_URL = 'https://allbirds-dataplane.rudderstack.com';
const RS_CONSENT_PROVIDER_ONE_TRUST = 'oneTrust';
const RS_SDK_BASE_URL = 'https://cdn.rudderlabs.com/v3';

// Pull resolved environment and shopId and get the App key
const { env, shopId } = window.ALLBIRDS.environment;
const rudderstackAppKey =
  RUDDERSTACK_APP_KEYS[`${env}-${shopId}`] || RUDDERSTACK_APP_KEYS[ENVIRONMENTS.DEVELOPMENT];

let initialized = false;

/**
 * Wrapper function to load the Rudderstack SDK with retry logic
 * Same function signature as the Rudderstack load function
 *
 * @param {string} rudderstackAppKey - Rudderstack App key
 * @param {string} RS_DATA_PLANE_URL - Rudderstack Data Plane URL
 * @param {object} loadOptions - Rudderstack load options
 */
const _loadRudderstackWithRetry = (appKey, dataPlaneUrl, loadOptions) => {
  const SDK_LOADED_ATTEMPTS = 60;
  const SDK_LOADED_ATTEMPTS_INTERVAL_MS = 1000;
  let loadsAttempts = 1;

  const intervalID = setInterval(function () {
    if (window.rudderanalytics && window.rudderanalytics.load) {
      window.rudderanalytics.load(appKey, dataPlaneUrl, loadOptions);

      clearInterval(intervalID);
    } else {
      if (loadsAttempts > SDK_LOADED_ATTEMPTS) {
        clearInterval(intervalID);

        DataDogLogs.error(
          `[Allbirds pixel] - Rudderstack SDK - could not be loaded - after ${loadsAttempts} attempts`,
          {},
          new Error('Rudderstack SDK could not be loaded'),
        );
      }

      loadsAttempts++;
    }
  }, SDK_LOADED_ATTEMPTS_INTERVAL_MS);
};

/**
 * Helper function to call the consent API based on OneTrust
 * This is part of the Rudderstack pre-consent user tracking approach
 *
 * @private
 */
const _callConsentAPI = (trackConsent) => {
  DataDogLogs.info('[Allbirds pixel] - Rudderstack SDK - call consent API', { trackConsent });

  try {
    window.rudderanalytics.consent({
      discardPreConsentEvents: false, // Optional; default value is false
      storage: {
        type: 'localStorage',
      },
      trackConsent: trackConsent || false, // Optional; default value is false
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack SDK - error calling consent API',
      { trackConsent },
      error,
    );
  }
};

/**
 * Wrapper function for window.rudderanalytics.load with options set to load the Rudderstack SDK
 */
const load = () => {
  if (!initialized) {
    // Load Options: https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/load-js-sdk/
    const loadOptions = {
      // The control plane endpoint serving your destination configurations.
      configUrl: RS_CONFIG_URL,

      // Enable consent management with OneTrust
      consentManagement: {
        enabled: true,
        provider: RS_CONSENT_PROVIDER_ONE_TRUST,
      },

      // URL used by the SDK to load its integration SDKs.
      destSDKBaseURL: `${RS_SDK_BASE_URL}/${window.rudderAnalyticsBuildType}/js-integrations`,
      // Load native integration with Amplitude and Google Analytics
      loadIntegration: false,

      // Internal log level of the SDK
      logLevel: window.ALLBIRDS.settings.rudderstack.logLevel || 'DEBUG',

      // Hook to load event
      onLoaded: () => {
        // Performances consent is used as gatekeeper to send data to Rudderstack
        const hasPerformanceConsent = window.ALLBIRDS.OneTrust.hasPerformanceConsent();

        // Initially call the consent API base on current OneTrust status
        _callConsentAPI(hasPerformanceConsent);

        // Orchestrate with OneTrust consent change for future User interaction with OneTrust
        window.ALLBIRDS.OneTrust.onConsentChange(() => {
          _callConsentAPI(hasPerformanceConsent);
        });

        DataDogLogs.info('[Allbirds pixel] - Rudderstack SDK - load event');
      },

      // Base URL path used by SDK to load the plugins.
      pluginsSDKBaseURL: `${RS_SDK_BASE_URL}/${window.rudderAnalyticsBuildType}/plugins`,

      // Option to track users as fully anonymous and flush the events after consent is given.
      preConsent: {
        enabled: true,
        events: {
          // Defines SDK's events delivery behavior, other accepted value is "immediate"
          delivery: 'buffer',
        },
        storage: {
          // Defines SDK's cookie storage strategy, other accepted values are "none", "session"
          strategy: 'session',
        },
      },

      // For retrocompatibility use the legacy encryption
      storage: {
        encryption: {
          version: 'legacy',
        },
      },
    };

    // Attempt to load Rudderstack SDK with retry logic
    _loadRudderstackWithRetry(rudderstackAppKey, RS_DATA_PLANE_URL, loadOptions);

    initialized = true;
  }
};

export { load };
